import { useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { Edit2, Loader2, MapPin, Plus, Trash2 } from 'lucide-react'
import { filialApi } from '@/services/api'
import { useAuthStore } from '@/stores/authStore'
import type { Filial, PaginatedResult } from '@/types/api'
import { Modal } from '@/components/ui/Modal'
import { useToast } from '@/components/ui/Toast'

const inputClass = 'w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-primary-500'
const labelClass = 'block text-sm font-medium text-slate-300 mb-1.5'

function Field({ label, name, value = '', required = false }: { label: string; name: string; value?: string; required?: boolean }) {
  return (
    <label>
      <span className={labelClass}>{label}{required && ' *'}</span>
      <input className={inputClass} name={name} defaultValue={value} required={required} />
    </label>
  )
}

function FilialModal({ empresaId, filial, onClose }: { empresaId: string; filial: Filial | null; onClose: () => void }) {
  const [saving, setSaving] = useState(false)
  const queryClient = useQueryClient()
  const { addToast } = useToast()

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const values = Object.fromEntries(new FormData(event.currentTarget))
    const address = {
      nome: String(values.nome),
      codigo: String(values.codigo).trim().toUpperCase(),
      idioma: String(values.idioma),
      cep: String(values.cep),
      rua: String(values.rua),
      numero: String(values.numero || ''),
      complemento: String(values.complemento || ''),
      bairro: String(values.bairro || ''),
      cidade: String(values.cidade),
      estado: String(values.estado),
      pais: String(values.pais).trim().toUpperCase()
    }

    setSaving(true)
    try {
      if (filial) {
        await filialApi.update(filial.id, { id: filial.id, ...address, ativa: values.ativa === 'on' })
      } else {
        await filialApi.create({ empresaId, ...address })
      }
      await queryClient.invalidateQueries({ queryKey: ['filiais', empresaId] })
      addToast({ type: 'success', title: filial ? 'Filial atualizada' : 'Filial cadastrada' })
      onClose()
    } catch (error) {
      const description = axios.isAxiosError(error)
        ? (error.response?.data?.detail || error.response?.data?.message || 'Verifique os dados informados.')
        : 'Não foi possível salvar a filial.'
      addToast({ type: 'error', title: 'Erro ao salvar filial', description: String(description) })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen onClose={onClose} title={filial ? 'Editar Filial' : 'Nova Filial'} size="lg">
      <form onSubmit={submit} className="space-y-4">
        <p className="text-sm text-slate-400">A filial será vinculada à empresa da sessão autenticada.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Nome" name="nome" value={filial?.nome} required />
          <Field label="Código" name="codigo" value={filial?.codigo} required />
          <label>
            <span className={labelClass}>Idioma *</span>
            <select className={inputClass} name="idioma" defaultValue={filial?.idioma || 'pt'}>
              <option value="pt">Português</option>
              <option value="en">English</option>
              <option value="es">Español</option>
            </select>
          </label>
          <Field label="CEP/ZIP Code" name="cep" value={filial?.cep} required />
          <Field label="Rua" name="rua" value={filial?.rua} required />
          <Field label="Número" name="numero" value={filial?.numero} />
          <Field label="Complemento" name="complemento" value={filial?.complemento} />
          <Field label="Bairro" name="bairro" value={filial?.bairro} />
          <Field label="Cidade" name="cidade" value={filial?.cidade} required />
          <Field label="Estado" name="estado" value={filial?.estado} required />
          <Field label="País (2 letras)" name="pais" value={filial?.pais || 'US'} required />
          {filial && (
            <label className="flex items-center gap-2 self-end pb-3 text-sm text-slate-300">
              <input type="checkbox" name="ativa" defaultChecked={filial.ativa} /> Filial ativa
            </label>
          )}
        </div>
        <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
          <button type="button" onClick={onClose} disabled={saving} className="px-4 py-2.5 rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700 disabled:opacity-50">Cancelar</button>
          <button type="submit" disabled={saving} className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Salvar
          </button>
        </div>
      </form>
    </Modal>
  )
}

export default function FiliaisPage() {
  const empresaId = useAuthStore(state => state.user?.empresaId)
  const [page, setPage] = useState(1)
  const [editing, setEditing] = useState<Filial | null | undefined>(undefined)
  const queryClient = useQueryClient()
  const { addToast } = useToast()

  const { data, isLoading } = useQuery<PaginatedResult<Filial>>({
    queryKey: ['filiais', empresaId, page],
    queryFn: () => filialApi.list(empresaId!, { page, pageSize: 10 }).then(response => response.data),
    enabled: Boolean(empresaId)
  })

  const remove = async (filial: Filial) => {
    if (!confirm(`Excluir a filial ${filial.nome}?`)) return
    try {
      await filialApi.delete(filial.id)
      await queryClient.invalidateQueries({ queryKey: ['filiais', empresaId] })
      addToast({ type: 'success', title: 'Filial excluída' })
    } catch {
      addToast({ type: 'error', title: 'Não foi possível excluir a filial' })
    }
  }

  if (!empresaId) {
    return (
      <div className="bg-amber-500/10 border border-amber-500/20 text-amber-300 rounded-xl p-6">
        O usuário autenticado não possui empresa vinculada. A administração de filiais está bloqueada.
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Filiais</h1>
          <p className="text-slate-400 mt-1">Gerenciar unidades da empresa autenticada</p>
        </div>
        <button onClick={() => setEditing(null)} className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2.5 rounded-lg font-medium">
          <Plus className="w-4 h-4" /> Nova Filial
        </button>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-800">
              <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">Filial</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">Código</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">Localização</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">Idioma</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">Status</th>
              <th className="text-right px-6 py-4 text-sm font-medium text-slate-400">Ações</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={6} className="px-6 py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary-500" /></td></tr>
            ) : !data?.items.length ? (
              <tr><td colSpan={6} className="px-6 py-12 text-center text-slate-500">Nenhuma filial cadastrada</td></tr>
            ) : data.items.map(filial => (
              <tr key={filial.id} className="border-b border-slate-800 hover:bg-slate-800/50">
                <td className="px-6 py-4"><div className="flex items-center gap-3"><MapPin className="w-5 h-5 text-primary-400" /><span className="text-white font-medium">{filial.nome}</span></div></td>
                <td className="px-6 py-4 text-slate-300">{filial.codigo}</td>
                <td className="px-6 py-4 text-slate-300">{filial.cidade}, {filial.estado} — {filial.pais}</td>
                <td className="px-6 py-4 text-slate-300">{filial.idioma.toUpperCase()}</td>
                <td className="px-6 py-4"><span className={filial.ativa ? 'text-emerald-400' : 'text-red-400'}>{filial.ativa ? 'Ativa' : 'Inativa'}</span></td>
                <td className="px-6 py-4"><div className="flex justify-end gap-2">
                  <button onClick={() => setEditing(filial)} className="p-2 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white" title="Editar"><Edit2 className="w-4 h-4" /></button>
                  <button onClick={() => remove(filial)} className="p-2 rounded-lg hover:bg-red-500/10 text-slate-400 hover:text-red-400" title="Excluir"><Trash2 className="w-4 h-4" /></button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
        {data && data.totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-800">
            <span className="text-sm text-slate-500">{data.totalCount} filiais</span>
            <div className="flex gap-2">
              <button disabled={page === 1} onClick={() => setPage(value => value - 1)} className="px-3 py-1.5 rounded-lg bg-slate-800 disabled:opacity-50">Anterior</button>
              <span className="px-3 py-1.5 text-slate-400">{page} de {data.totalPages}</span>
              <button disabled={page === data.totalPages} onClick={() => setPage(value => value + 1)} className="px-3 py-1.5 rounded-lg bg-slate-800 disabled:opacity-50">Próxima</button>
            </div>
          </div>
        )}
      </div>

      {editing !== undefined && <FilialModal empresaId={empresaId} filial={editing} onClose={() => setEditing(undefined)} />}
    </div>
  )
}
